import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  LineChart,
  Line,
  ComposedChart,
} from "recharts";
import { TrendUp, TrendDown, ChartLineUp, Calendar } from "@phosphor-icons/react";

const SCOPE_COLORS = { scope1: "#274E37", scope2: "#1A5F7A", scope3: "#D97706" };

export default function Trends() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [targets, setTargets] = useState([]);

  const load = async () => {
    const [y, tg] = await Promise.all([
      api.get("/yoy"),
      api.get("/targets"),
    ]);
    setRows(y.data);
    setTargets(tg.data);
  };

  useEffect(() => { load(); }, []);

  const summary = useMemo(() => {
    if (rows.length < 2) return null;
    const latest = rows[rows.length - 1];
    const first = rows[0];
    const cagr = first.total > 0 ?
      (Math.pow(latest.total / first.total, 1 / (rows.length - 1)) - 1) * 100 : null;
    return {
      first_year: first.year,
      latest_year: latest.year,
      first_total: first.total,
      latest_total: latest.total,
      change_pct: first.total > 0 ? ((latest.total - first.total) / first.total) * 100 : null,
      cagr,
      years: rows.length,
    };
  }, [rows]);

  // Projection line based on the highest-priority target
  const withProjection = useMemo(() => {
    if (!rows.length) return rows;
    const nearTerm = targets.find((t) => t.status === "near-term") || targets[0];
    if (!nearTerm) return rows;
    const base = rows.find((r) => r.year === nearTerm.baseline_year) || rows[0];
    const target_val = base.total * (1 - (nearTerm.reduction_pct / 100));
    const projection = [];
    for (let y = base.year; y <= nearTerm.target_year; y++) {
      const t_frac = (y - base.year) / (nearTerm.target_year - base.year || 1);
      const proj = base.total + (target_val - base.total) * t_frac;
      projection.push({ year: y, target: parseFloat(proj.toFixed(3)) });
    }
    // Merge target into rows by year
    const byYear = {};
    rows.forEach((r) => { byYear[r.year] = { ...r }; });
    projection.forEach((p) => {
      byYear[p.year] = byYear[p.year] || { year: p.year };
      byYear[p.year].target = p.target;
    });
    return Object.values(byYear).sort((a, b) => a.year - b.year);
  }, [rows, targets]);

  return (
    <div className="space-y-6" data-testid="trends-page">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Trends · Multi-Year</div>
        <h1 className="font-display text-3xl font-semibold mt-1 flex items-center gap-2">
          <ChartLineUp size={28} weight="duotone" className="text-primary" />
          {t("trendsTitle")}
        </h1>
        <p className="text-muted-foreground text-sm mt-2">{t("trendsDesc")}</p>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="trend-kpi-first">
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-1">
                <Calendar size={12} />
                {t("trendBaseline")} · {summary.first_year}
              </div>
              <div className="font-display text-2xl font-semibold mt-2 font-mono-data">
                {summary.first_total.toFixed(2)}<span className="text-sm text-muted-foreground ml-1">tCO₂e</span>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="trend-kpi-latest">
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-1">
                <Calendar size={12} />
                {t("trendLatest")} · {summary.latest_year}
              </div>
              <div className="font-display text-2xl font-semibold mt-2 font-mono-data">
                {summary.latest_total.toFixed(2)}<span className="text-sm text-muted-foreground ml-1">tCO₂e</span>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="trend-kpi-change" className={summary.change_pct != null && summary.change_pct < 0 ? "border-primary/40" : summary.change_pct != null && summary.change_pct > 0 ? "border-destructive/40" : ""}>
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-1">
                {summary.change_pct != null && summary.change_pct < 0 ? <TrendDown size={12} weight="fill" className="text-primary" /> : <TrendUp size={12} weight="fill" className="text-destructive" />}
                {t("trendTotalChange")}
              </div>
              <div className={`font-display text-2xl font-semibold mt-2 font-mono-data ${summary.change_pct != null && summary.change_pct < 0 ? "text-primary" : summary.change_pct != null && summary.change_pct > 0 ? "text-destructive" : ""}`}>
                {summary.change_pct == null ? "—" : `${summary.change_pct > 0 ? "+" : ""}${summary.change_pct.toFixed(1)}%`}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="trend-kpi-cagr">
            <CardContent className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">
                CAGR · {summary.years} {t("trendYears")}
              </div>
              <div className={`font-display text-2xl font-semibold mt-2 font-mono-data ${summary.cagr != null && summary.cagr < 0 ? "text-primary" : ""}`}>
                {summary.cagr == null ? "—" : `${summary.cagr > 0 ? "+" : ""}${summary.cagr.toFixed(1)}%`}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stacked scopes chart */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">{t("trendByScope")}</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">{t("trendNoData")}</div>
          ) : (
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8E4" />
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} unit=" t" />
                  <Tooltip formatter={(v) => `${(+v).toFixed(3)} tCO₂e`} />
                  <Legend />
                  <Bar dataKey="scope1" stackId="a" fill={SCOPE_COLORS.scope1} name="Scope 1" />
                  <Bar dataKey="scope2" stackId="a" fill={SCOPE_COLORS.scope2} name="Scope 2" />
                  <Bar dataKey="scope3" stackId="a" fill={SCOPE_COLORS.scope3} name="Scope 3" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trajectory vs target */}
      {targets.length > 0 && withProjection.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg">{t("trendVsTarget")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={withProjection}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8E4" />
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} unit=" t" />
                  <Tooltip formatter={(v) => `${(+v).toFixed(3)} tCO₂e`} />
                  <Legend />
                  <Line dataKey="total" stroke="#274E37" strokeWidth={2.5} name={t("trendActual")} dot={{ r: 3 }} />
                  <Line dataKey="target" stroke="#D97706" strokeWidth={2} strokeDasharray="5 5" name={t("trendTargetPath")} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">{t("trendTable")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-4 py-2">{t("year")}</th>
                <th className="text-right px-4 py-2">Scope 1</th>
                <th className="text-right px-4 py-2">Scope 2</th>
                <th className="text-right px-4 py-2">Scope 3</th>
                <th className="text-right px-4 py-2">{t("total")}</th>
                <th className="text-right px-4 py-2">YoY</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.year} className="border-b border-border/60" data-testid={`trend-row-${r.year}`}>
                  <td className="px-4 py-2 font-mono-data font-medium">{r.year}</td>
                  <td className="px-4 py-2 text-right font-mono-data">{r.scope1.toFixed(3)}</td>
                  <td className="px-4 py-2 text-right font-mono-data">{r.scope2.toFixed(3)}</td>
                  <td className="px-4 py-2 text-right font-mono-data">{r.scope3.toFixed(3)}</td>
                  <td className="px-4 py-2 text-right font-mono-data font-medium">{r.total.toFixed(3)}</td>
                  <td className={`px-4 py-2 text-right font-mono-data ${r.yoy_change_pct == null ? "" : r.yoy_change_pct < 0 ? "text-primary" : "text-destructive"}`}>
                    {r.yoy_change_pct == null ? "—" : `${r.yoy_change_pct > 0 ? "+" : ""}${r.yoy_change_pct}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
